import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  User,
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  Gift,
  Users,
  TrendingUp,
  DollarSign,
  Calendar,
  MapPin,
  RefreshCw,
  Download,
} from "lucide-react";
import api from "../utils/axios";
import formatCurrency from "../helper/formatCurrency";

type UserProfile = {
  uid: string | number;
  name: string;
  email: string;
  status: string;
  created: string;
  balance: number;
  total_deposit: number;
  total_withdrawal: number;
  bonus: number;
  actual_total_deposit_amount: number;
  actual_total_withdrawal_amount: number;
  total_bet_level_commission: number;
  total_deposit_level_commission: number;
  total_referral_reward: number;
  total_bet_amount: number;
  total_signup_bonus_amount: number;
  ip?: string;
  daily_code?: string;
  f1?: number;
  f2?: number;
  f3?: number;
  f4?: number;
  f5?: number;
  f6?: number;
  total_referrals?: number;
  roses_today?: number;
  total_commission?: number;
  referalcode?: string;
  total_deposit_bonus_amount: number;
};


const tabs = [
  { key: "deposit", label: "Deposits", icon: ArrowUpCircle },
  { key: "withdrawal", label: "Withdrawals", icon: ArrowDownCircle },
  { key: "bets", label: "Bets", icon: Wallet },
  { key: "bonus", label: "Bonus", icon: Gift },
];

const UserDetail: React.FC = () => {
  let uid = useParams<{ uid: string }>();

  uid = uid?.id

  const [user, setUser] = useState<UserProfile | null>(null);
  const [activeTab, setActiveTab] = useState("deposit");
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(true);


  const [depositRows, setDepositRows] = useState<any[]>([]);
  const [withdrawalRows, setWithdrawalRows] = useState<any[]>([]);
  const [betRows, setBetRows] = useState<any[]>([]);
  const [bonusRows, setBonusRows] = useState<any[]>([]);

  /* ---------------- Fetch User ---------------- */
  const fetchUser = async () => {
    try {
      const res = await api.get(`/user/${uid}/detail`);
      const { user, wallet } = res.data;

      setUser({
        uid: user.uid,
        name: user.name,
        email: user.email,
        status: user.status ?? "active",
        created: user.created ?? "",
        referalcode: user.referalcode ?? "",
        balance: Math.max(0, Number(wallet.balance) || 0),
        total_deposit: Number(wallet.total_deposit) || 0,
        total_withdrawal: Number(wallet.total_withdrawal) || 0,
        bonus: Number(wallet.total_bonus) || 0,
        actual_total_deposit_amount:
          Number(user.actual_total_deposit_amount) || 0,
        actual_total_withdrawal_amount:
          Number(user.actual_total_withdrawal_amount) || 0,
        total_bet_level_commission:
          Number(user.total_bet_level_commission) || 0,
        total_deposit_level_commission:
          Number(user.total_deposit_level_commission) || 0,
        total_referral_reward: Number(user.total_referral_reward) || 0,
        total_bet_amount: Number(user.total_bet_amount) || 0,
        total_signup_bonus_amount: Number(user.total_signup_bonus_amount) || 0,
        total_deposit_bonus_amount: Number(user.total_deposit_bonus_amount) || 0,
        ip: user.ip || "N/A",
        daily_code: user.daily_code || "000000",
        f1: user?.members?.counts?.f1 || 0,
        f2: user?.members?.counts?.f2 || 0,
        f3: user?.members?.counts?.f3 || 0,
        f4: user?.members?.counts?.f4 || 0,
        f5: user?.members?.counts?.f5 || 0,
        f6: user?.members?.counts?.f6 || 0,
        total_referrals: user?.members?.counts?.total || 0,
        roses_today: user.roses_today || 0,
        total_commission: user.total_commission || 0,
      });
    } catch (err) {
      console.error("User fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Fetch Tab Data ---------------- */
  const fetchTabData = async () => {
    setTabLoading(true);
    try {
      const res = await api.get(
        `/user/${uid?.id}/transactions?req_type=${activeTab}&status=${status}`
      );
      setRows(res.data.data || res.data || []);
    } catch (err) {
      console.error("Tab fetch failed", err);
      setRows([]);
    } finally {
      setTabLoading(false);
    }
  };


   /* ================= TAB FETCHERS ================= */

  const fetchDeposits = async () => {
    setTabLoading(true);
    try {
      const res = await api.get(
        `/user/${uid}/deposits?status=${status}`
      );
      setDepositRows(res.data.data || []);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchWithdrawals = async () => {
    setTabLoading(true);
    try {
      const res = await api.get(
        `/user/${uid}/withdrawals?status=${status}`
      );
      setWithdrawalRows(res.data.data || []);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchBets = async () => {
    setTabLoading(true);
    try {
      const res = await api.get(`/user/${uid}/bets`);
      setBetRows(res.data.data || []);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchBonuses = async () => {
    setTabLoading(true);
    try {
      const res = await api.get(`/user/${uid}/bonuses`);
      setBonusRows(res.data.data || []);
    } finally {
      setTabLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    fetchTabData();
  }, [activeTab, status]);

  useEffect(() => {
    if (activeTab === "deposit") fetchDeposits();
    if (activeTab === "withdrawal") fetchWithdrawals();
    if (activeTab === "bets") fetchBets();
    if (activeTab === "bonus") fetchBonuses();
  }, [activeTab, status]);

  // if (loading || !user) return <div className="p-10">Loading...</div>;

  const tableData =
    activeTab === "deposit"
      ? depositRows
      : activeTab === "withdrawal"
      ? withdrawalRows
      : activeTab === "bets"
      ? betRows
      : bonusRows;


  if (loading || !user) {
    return (
      <div className="min-h-screen ">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="h-32 bg-white rounded-2xl animate-pulse shadow-sm" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="h-96 bg-white rounded-2xl animate-pulse shadow-sm" />
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="h-24 bg-white rounded-xl animate-pulse shadow-sm"
                  />
                ))}
              </div>
              <div className="h-96 bg-white rounded-2xl animate-pulse shadow-sm" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-600 to-gray-600 bg-clip-text text-transparent">
                User Profile
              </h1>
              <p className="text-slate-500 mt-1">
                Comprehensive user details and transaction history
              </p>
            </div>
            <button
              onClick={fetchUser}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-md hover:shadow-lg"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ================= LEFT SIDEBAR - USER INFO ================= */}
          <div className="space-y-6">
            {/* User Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-br from-blue-600 to-blue-600 p-4 text-center">
                <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center text-3xl font-bold mx-auto mb-4 shadow-xl border-4 border-white/30">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <h2 className="text-2xl font-bold text-white">{user.name}</h2>
                <p className="text-gray-100 text-sm mt-1">{user.email}</p>
                <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-white/80 text-sm">UID:</span>
                  <span className="text-white font-semibold">{user.uid}</span>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <InfoRow
                  label="Balance"
                  value={formatCurrency(user.balance)}
                  valueClass="text-green-600 font-bold"
                />
                <InfoRow
                  label="Total Deposit"
                  value={formatCurrency(user.total_deposit)}
                />
                <InfoRow
                  label="Total Withdraw"
                  value={formatCurrency(user.total_withdrawal)}
                  valueClass="text-red-600"
                />
                <InfoRow
                  label="Total Commission"
                  value={formatCurrency(user.total_commission)}
                  valueClass="text-gray-600"
                />
              </div>
            </div>

            {/* Referral Network */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Users size={20} className="text-blue-600" />
                Referral Network
              </h3>
              <div className="space-y-3">
                <ReferralLevel level="F1" count={user.f1 || 0} />
                <ReferralLevel level="F2" count={user.f2 || 0} />
                <ReferralLevel level="F3" count={user.f3 || 0} />
                <ReferralLevel level="F4" count={user.f4 || 0} />
                <ReferralLevel level="F5" count={user.f5 || 0} />
                <ReferralLevel level="F6" count={user.f6 || 0} />
                <div className="pt-3 border-t border-slate-200">
                  <InfoRow
                    label="Total Invitations"
                    value={`${user.total_referrals || 0} members`}
                    valueClass="font-bold text-gray-600"
                  />
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-800 mb-4">
                Additional Info
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <MapPin size={16} className="text-slate-400" />
                  <span className="text-slate-600">IP Address:</span>
                  <span className="font-mono text-slate-800 ml-auto">
                    {user.ip}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Calendar size={16} className="text-slate-400" />
                  <span className="text-slate-600">Referral Code:</span>
                  <span className="font-mono text-slate-800 ml-auto">
                    {user.referalcode}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Calendar size={16} className="text-slate-400" />
                  <span className="text-slate-600">Joined:</span>
                  <span className="text-slate-800 ml-auto">
                    {new Date(user.created).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ================= RIGHT CONTENT - STATS & TABS ================= */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Balance"
                value={formatCurrency(user.balance)}
                icon={Wallet}
                gradient="from-green-500 to-emerald-600"
              />
              <StatCard
                title="Total Deposit"
                value={formatCurrency(user.actual_total_deposit_amount)}
                icon={ArrowUpCircle}
                gradient="from-blue-500 to-gray-600"
              />
              <StatCard
                title="Total Withdrawal"
                value={formatCurrency(user.actual_total_withdrawal_amount)}
                icon={ArrowDownCircle}
                gradient="from-red-500 to-rose-600"
              />
              <StatCard
                title="Total Bets"
                value={formatCurrency(user.total_bet_amount)}
                icon={TrendingUp}
                gradient="from-gray-500 to-pink-600"
              />
              <StatCard
                title="Signup Bonus"
                value={formatCurrency(user.total_signup_bonus_amount)}
                icon={Gift}
                gradient="from-amber-500 to-orange-600"
              />
              <StatCard
                title="Deposit Bonus"
                value={formatCurrency(user.total_deposit_bonus_amount)}
                icon={Gift}
                gradient="from-green-500 to-green-600"
              />
              <StatCard
                title="Referral Bonus"
                value={formatCurrency(user.total_referral_reward)}
                icon={Users}
                gradient="from-cyan-500 to-blue-600"
              />
              <StatCard
                title="Deposit Commission"
                value={formatCurrency(user.total_deposit_level_commission)}
                icon={DollarSign}
                gradient="from-teal-500 to-green-600"
              />
              <StatCard
                title="Bet Commission"
                value={formatCurrency(user.total_bet_level_commission)}
                icon={TrendingUp}
                gradient="from-violet-500 to-gray-600"
              />
            </div>

            {/* Transactions Tabs */}
            {/* ===== TABS ===== */}
     <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              {/* Tab Headers */}
              <div className="flex border-b border-slate-200 bg-slate-50 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-6 py-4 flex items-center gap-2 ${
                  activeTab === t.key
                    ? "border-b-2 border-gray-600 font-bold"
                    : ""
                }`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Status filter only for deposit/withdraw */}
        {(activeTab === "deposit" || activeTab === "withdrawal") && (
          <div className="p-4 flex gap-2">
            {["pending", "accepted", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1 rounded ${
                  status === s ? "bg-gray-600 text-white" : "border"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* ===== TABLE ===== */}
        <div className="p-4 overflow-x-auto">
          {tabLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-gray-600"></div>
            </div>
          ) : tableData.length === 0 ? (
            <div className="text-center text-gray-500 py-8">No records found</div>
          ) : activeTab === "bets" ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-slate-600">
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Game / Provider</th>
                  <th className="text-left p-3">Bet Amount</th>
                  <th className="text-left p-3">Win Amount</th>
                  <th className="text-left p-3">Result</th>
                  <th className="text-left p-3">Round ID</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => {
                  const betAmt = Number(row.bet_amount) || 0;
                  const winAmt = Number(row.win_amount) || 0;
                  const diff = winAmt - betAmt;
                  const isWin = winAmt > 0;
                  const dateStr = row.bet_timestamp || row.created_at || row.transaction_date;
                  const formattedDate = dateStr ? new Date(dateStr).toLocaleString() : "N/A";

                  return (
                    <tr key={row.id || i} className="border-b hover:bg-slate-50/50">
                      <td className="p-3 text-slate-600 text-xs">
                        {formattedDate}
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-slate-800">{row.game_name || "Game"}</div>
                        <div className="text-xs text-slate-400">{row.game_provider || "Provider"}</div>
                      </td>
                      <td className="p-3 font-semibold text-slate-800">
                        {formatCurrency(betAmt)}
                      </td>
                      <td className="p-3 font-semibold text-green-600">
                        {formatCurrency(winAmt)}
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            isWin
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {isWin ? `Win (+${formatCurrency(diff)})` : "Loss"}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-slate-400 font-mono">
                        {row.game_round || row.serial_number || "N/A"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : activeTab === "bonus" ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-slate-600">
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Bonus Type</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => {
                  const amount = Number(row.bonus_amount || row.amount) || 0;
                  const dateStr = row.created_at || row.created || row.today;
                  const formattedDate = dateStr ? new Date(dateStr).toLocaleString() : "N/A";

                  return (
                    <tr key={row.id || i} className="border-b hover:bg-slate-50/50">
                      <td className="p-3 text-slate-600 text-xs">
                        {formattedDate}
                      </td>
                      <td className="p-3 font-medium capitalize text-slate-800">
                        {row.bonus_type || row.type || "Bonus"}
                      </td>
                      <td className="p-3 font-semibold text-green-600">
                        {formatCurrency(amount)}
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          {row.status || "Credited"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-slate-600">
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">UTR / Method</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => {
                  const amount = Number(row.amount) || 0;
                  const dateStr = row.created_at || row.transaction_date || row.today;
                  const formattedDate = dateStr ? new Date(dateStr).toLocaleString() : "N/A";
                  const statusVal = (row.status || "pending").toLowerCase();

                  return (
                    <tr key={row.transaction_id || row.id || i} className="border-b hover:bg-slate-50/50">
                      <td className="p-3 text-slate-600 text-xs">
                        {formattedDate}
                      </td>
                      <td className="p-3 font-semibold text-slate-800">
                        {formatCurrency(amount)}
                      </td>
                      <td className="p-3 text-xs text-slate-600 font-mono">
                        {row.utr_no || row.transaction_type || row.payment_type || "N/A"}
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            statusVal === "accepted" || statusVal === "success"
                              ? "bg-green-100 text-green-700"
                              : statusVal === "rejected" || statusVal === "failed"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {row.status || "pending"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      

          </div>
        </div>
      </div>
    </div>
  );
};

/* ================= COMPONENTS ================= */

const StatCard = ({
  title,
  value,
  icon: Icon,
  gradient,
}: {
  title: string;
  value: string;
  icon: any;
  gradient: string;
}) => (
  <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5 hover:shadow-lg transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <div
        className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md`}
      >
        <Icon size={20} className="text-white" />
      </div>
    </div>
    <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">
      {title}
    </p>
    <p className="text-xl font-bold text-slate-800 mt-1">{value}</p>
  </div>
);

const InfoRow = ({
  label,
  value,
  valueClass = "text-slate-800",
}: {
  label: string;
  value: string;
  valueClass?: string;
}) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-600 font-medium">{label}</span>
    <span className={`text-sm ${valueClass}`}>{value}</span>
  </div>
);

const ReferralLevel = ({ level, count }: { level: string; count: number }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
        {level}
      </div>
      <span className="text-sm text-slate-600 font-medium">{level}</span>
    </div>
    <span className="text-sm font-bold text-slate-800">
      {count} {count === 1 ? "member" : "members"}
    </span>
  </div>
);

export default UserDetail;